import React, { useState } from 'react';
import { OpenInNewIcon } from 'components/Icons';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { message as antMessage } from 'antd';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes/paths';
import { RecordSource } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useOperationEvents from 'screens/Applications&WorkOrders/Hooks/useOperationEvents';
import { RECORD_SOURCE_MESSAGE_KEY } from './constants/messageKey';
import MessageContent from './components/MessageContent';
import useRecordValues from './hooks/useRecordValues';

interface RecordSourceButtonProps {
  recordSource: RecordSource;
  type: OperationType;
}

const RecordSourceButton: React.FC<RecordSourceButtonProps> = ({
  recordSource,
  type
}) => {
  const {
    icon,
    isClickable,
    tooltipTitle,
    recordSourceName = recordSource.recordSourceName,
    onClick
  } = useRecordValues(recordSource);

  const history = useHistory();
  const { sendAccessRecordSourceEvent } = useOperationEvents(type);

  const handleClick = () => {
    if (isClickable) {
      sendAccessRecordSourceEvent(recordSource.recordSourceType, type);

      const onReturn = () => {
        if (type === OperationType.APPLICATION) {
          history.push(paths.applications);
        } else {
          history.push(paths.workOrders);
        }
      };

      onClick?.();
      const messageContent = <MessageContent type={type} onReturn={onReturn} />;

      antMessage.success({
        key: RECORD_SOURCE_MESSAGE_KEY,
        className: 'ant-message-dark-mode br-4',
        content: messageContent,
        duration: 0,
        icon: <></>
      });
    }
  };

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isDeleted = recordSource.deleted;

  if (isClickable) {
    return (
      <div
        style={{ height: '20px', position: 'relative' }}
        className="flex items-center gap-2"
      >
        <div className="flex items-center min-w-0">
          <Tooltip
            title={tooltipTitle}
            placement="top"
            align={{ offset: [0, 2] }}
            arrowPointAtCenter
            overlayInnerStyle={{
              backgroundColor: '#333'
            }}
            open={isTooltipOpen}
          >
            <span
              style={{
                position: 'absolute',
                left: '9px',
                top: 0,
                width: '1px',
                height: '100%',
                pointerEvents: 'none'
              }}
            />
          </Tooltip>
          <button
            type="button"
            onClick={isDeleted ? undefined : handleClick}
            onMouseEnter={() =>
              isDeleted ? undefined : setIsTooltipOpen(true)
            }
            onMouseLeave={() =>
              isDeleted ? undefined : setIsTooltipOpen(false)
            }
            style={{
              height: '24px',
              left: '-0.5rem',
              marginRight: '-0.5rem',
              position: 'relative'
            }}
            className={`flex items-center gap-1 px-2 py-0.5 rounded overflow-clip min-w-0 ${
              isDeleted
                ? 'bg-neutral-10 focus:outline-none cursor-default'
                : 'hover:bg-blue-10 cursor-pointer'
            } transition-colors`}
          >
            <div className="flex items-center flex-shrink-0">{icon}</div>
            <div
              className={`text-sm min-w-0 flex-1 truncate ${
                isDeleted ? 'text-neutral-50' : 'text-blue-60'
              } font-normal leading-5 tracking-tight`}
            >
              {recordSourceName}
            </div>
            {!isDeleted && (
              <div className="flex items-center flex-shrink-0">
                <OpenInNewIcon fill="#0071CD" />
              </div>
            )}
          </button>
        </div>
        {isDeleted && (
          <div className="flex items-center text-sm text-neutral-70 font-normal leading-5 tracking-tight whitespace-nowrap">
            (Deleted)
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 py-0.5 rounded overflow-clip min-w-0">
      <div className="flex items-center flex-shrink-0">{icon}</div>
      <div className="text-sm text-neutral-1000 font-normal leading-5 tracking-tight min-w-0 flex-1 truncate">
        {recordSourceName}
      </div>
    </div>
  );
};

export default RecordSourceButton;
